import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  LocalShipping,
  Tag,
  Lock,
  Straighten,
  MonitorWeight,
  Build,
  Person,
  DirectionsCarFilled,
  Print as PrintIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { t } from "i18next";

const CollapseView = ({ row, onOpenContainerPrint }) => {
  const containers = row?.containers || [];
  const shipmentMode = row?.shipmentMode || row?.type;
  const exporter = row?.exporter;
  const primaryClient = {
    id: row?.primaryClientId,
    name: row?.primaryClientName,
    company: row?.primaryClientCompany,
  };

  const formatAddress = (obj) => [obj?.street, obj?.city, obj?.country].filter(Boolean).join(", ");
  const vehicleDescription = (v) =>
    [v?.vehicleType?.name || v?.vehicleType, v?.year, v?.make?.name || v?.make, v?.model?.name || v?.model]
      .filter(Boolean)
      .join(" ");
  const vehicleColor = (v) => v?.color?.name || v?.color;
  const bookingDestination = row?.booking?.destination?.name || row?.booking?.destination;
  const vehicleDestination = (v, container) => bookingDestination || container?.destination || v?.destination?.name || v?.destination;
  const getItemClient = (item) => {
    return {
      id: item?.client?.id || item?.vehicle?.clientId || primaryClient?.id,
      name: item?.client?.name || item?.vehicle?.clientName || primaryClient?.name,
      company: item?.client?.company || item?.vehicle?.clientCompany || primaryClient?.company,
    };
  };
  const getBillOfLadingLabel = (files) => {
    if (!files?.length) return "-";
    return files.map((file) => file?.fileName || file?.name || file?.originalFileName).filter(Boolean).join(", ") || t("Attached");
  };
  const renderPartyBlock = (title, party, numberLabel = t("Reference #")) => {
    if (!party) return null;

    const clipText = {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
      maxWidth: "100%",
    };
    const address = formatAddress(party);

    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0B3C5D" }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={clipText} title={party.name || ""}>
          {party.name}
        </Typography>
        <Typography variant="caption" display="block" sx={clipText} title={party.consigneeNumber || party.exportNumber || ""}>
          <strong>{numberLabel}:</strong> {party.consigneeNumber || party.exportNumber || "-"}
        </Typography>
        {party.phoneNumber && (
          <Typography variant="caption" display="block" sx={clipText} title={party.phoneNumber}>
            {t("Phone")}: {party.phoneNumber}
          </Typography>
        )}
        {party.email && (
          <Typography variant="caption" display="block" sx={clipText} title={party.email}>
            {t("Email")}: {party.email}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, ...clipText }} title={address}>
          {address}
        </Typography>
      </Box>
    );
  };
  const handleCopy = (text) => {
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
  };

  const renderVehicleStat = (label, value, copyValue) => (
    <Box
      sx={{
        minWidth: 0,
        p: 1,
        border: "1px solid #eef2f5",
        borderRadius: 1.5,
        bgcolor: "#fff",
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap title={value || "-"}>
          {value ?? "-"}
        </Typography>
        {copyValue && (
          <Tooltip title={t("Copy VIN")} arrow>
            <IconButton size="small" onClick={() => handleCopy(copyValue)} sx={{ p: 0.25 }}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  const renderVehicleDetailsCard = (item, itemIndex, container) => {
    const vehicle = item?.vehicle;
    if (!vehicle) return null;

    const itemClient = getItemClient(item);
    const desc = vehicleDescription(vehicle) || t("Vehicle");
    const vin = vehicle?.vin;

    return (
      <Box
        key={item?.id || vehicle?.id || itemIndex}
        sx={{
          flex: { xs: "0 0 88vw", md: "0 0 560px", lg: "0 0 620px" },
          width: { xs: "88vw", md: 560, lg: 620 },
          p: 1.5,
          border: "1px solid #dbe7f3",
          borderRadius: 2,
          backgroundColor: "#fbfdff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: "#E1F0FF",
              color: "#0B3C5D",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "0 0 auto",
            }}
          >
            <DirectionsCarFilled fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0B3C5D" }} noWrap title={desc}>
              {t("Vehicle")} #{itemIndex + 1}: {desc}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {itemClient?.name ? `${t("Client")}: ${itemClient.name}${itemClient.company ? ` (${itemClient.company})` : ""}` : t("Client") + ": -"}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 1,
            mb: 1.5,
          }}
        >
          {renderVehicleStat(t("VIN"), vin, vin)}
          {renderVehicleStat(t("Client"), itemClient?.name ? `${itemClient.name}${itemClient.company ? ` (${itemClient.company})` : ""}` : "-")}
          {renderVehicleStat(t("Weight"), vehicle?.weight)}
          {renderVehicleStat(t("Fuel"), vehicle?.fuelType?.name || vehicle?.fuelType)}
          {renderVehicleStat(t("Color"), vehicleColor(vehicle))}
          {renderVehicleStat(t("Destination"), vehicleDestination(vehicle, container))}
          {renderVehicleStat(t("Price"), vehicle?.price)}
          {renderVehicleStat(t("Description"), item?.description || row?.primaryDescription || "-")}
          {renderVehicleStat(t("Bill Of Lading"), getBillOfLadingLabel(item?.billOfLadingFile))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 1.5,
            pt: 1.5,
            borderTop: "1px solid #eef2f5",
          }}
        >
          {renderPartyBlock(t("Consignee"), item?.consignee, t("Consignee #")) || (
            <Typography variant="body2" color="text.secondary">{t("Consignee")}: -</Typography>
          )}
          {renderPartyBlock(t("Notify Party"), item?.notifyParty, t("Notify Party #")) || (
            <Typography variant="body2" color="text.secondary">{t("Notify Party")}: -</Typography>
          )}
          {renderPartyBlock(t("Consignee Dock"), item?.consigneeDock, t("Consignee Dock #")) || (
            <Typography variant="body2" color="text.secondary">{t("Consignee Dock")}: -</Typography>
          )}
        </Box>
      </Box>
    );
  };

  if (!containers.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t("No containers")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: "#F7F9FB" }}>
      {containers.map((c, ci) => {
        const shipmentItems = c?.shipmentItems || [];
        const vehicles = shipmentItems.map((item) => item?.vehicle).filter(Boolean);
        const firstShipmentItem = shipmentItems[0];
        const sharedConsignee = shipmentMode === "FCL" ? firstShipmentItem?.consignee : null;
        const sharedNotifyParty = shipmentMode === "FCL" ? firstShipmentItem?.notifyParty : null;
        const sharedConsigneeDock = shipmentMode === "FCL" ? firstShipmentItem?.consigneeDock : null;
        const containerClient = shipmentMode === "LCL" ? getItemClient(firstShipmentItem) : primaryClient;

        return (
          <Paper
            key={c.id || ci}
            elevation={0}
            sx={{ mb: 3, p: 2, border: "1px solid #e2e8f0", borderRadius: 3, bgcolor: "#fff" }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1, alignItems: "center" }}>
              {(() => {
                const pillBase = {
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: "16px",
                  fontSize: 12,
                  lineHeight: 1.2,
                  fontWeight: 500,
                  maxWidth: 220,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  border: "1px solid transparent",
                };

                const pills = [
                  {
                    show: true,
                    icon: <LocalShipping fontSize="inherit" />,
                    label: `${t("Container")} #${ci + 1}`,
                    sx: { bgcolor: "#E1F0FF", color: "#0B3C5D", border: "1px solid #B3DAFF", fontWeight: 600 },
                  },
                  {
                    show: !!c.containerNumber,
                    icon: <Tag fontSize="inherit" />,
                    label: `${t("Number")}: ${c.containerNumber}`,
                    sx: { bgcolor: "#F4F6F8" },
                  },
                  {
                    show: !!c.sealNumber,
                    icon: <Lock fontSize="inherit" />,
                    label: `${t("Seal")}: ${c.sealNumber}`,
                    sx: { bgcolor: "#F4F6F8" },
                  },
                  {
                    show: !!c.containerSize,
                    icon: <Straighten fontSize="inherit" />,
                    label: `${t("Size")}: ${c.containerSize}`,
                    sx: { bgcolor: "#F4F6F8" },
                  },
                  {
                    show: true,
                    icon: <MonitorWeight fontSize="inherit" />,
                    label: `${t("Net Weight")}: ${c.netWeight ?? 0}`,
                    sx: { bgcolor: "#E6F7FF", color: "#055160", border: "1px solid #B6E3F5" },
                  },
                  {
                    show: true,
                    icon: <Build fontSize="inherit" />,
                    label: `${t("Parts")}: ${c.partsInContainer ? t("Yes") : t("No")}`,
                    sx: c.partsInContainer
                      ? { bgcolor: "#E8F6EE", color: "#1B5E20", border: "1px solid #A5D6A7" }
                      : { bgcolor: "#F9F9F9", color: "#555", border: "1px dashed #D0D7DE" },
                  },
                  {
                    show: !!containerClient?.name,
                    icon: <Person fontSize="inherit" />,
                    label: `${t("Client")}: ${containerClient?.name || "-"}`,
                    sx: { bgcolor: "#F2ECFF", color: "#5E35B1", border: "1px solid #D4C2F8" },
                  },
                ];

                const nodes = pills
                  .filter((p) => p.show)
                  .map((p, i) => (
                    <Tooltip key={i} title={p.label} arrow>
                      <Box sx={{ ...pillBase, ...p.sx }}>
                        <Box sx={{ fontSize: 14, display: "flex", alignItems: "center" }}>{p.icon}</Box>
                        <span>{p.label}</span>
                      </Box>
                    </Tooltip>
                  ));

                nodes.push(
                  <Tooltip key="print" title={t("Print Container")} arrow>
                    <IconButton size="small" onClick={() => onOpenContainerPrint(ci, row)} sx={{ ml: 0.5 }}>
                      <PrintIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                );

                return nodes;
              })()}
            </Box>

            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                display: "grid",
                width: "50%",
                gridTemplateColumns: { xs: "repeat(auto-fill,minmax(220px,1fr))", md: "repeat(2,1fr)" },
                gap: 2,
                mb: 2,
              }}
            >
              {renderPartyBlock(t("Exporter"), exporter, t("Export #"))}
              {shipmentMode === "FCL" && renderPartyBlock(t("Consignee"), sharedConsignee, t("Consignee #"))}
              {shipmentMode === "FCL" && renderPartyBlock(t("Notify Party"), sharedNotifyParty, t("Notify Party #"))}
              {shipmentMode === "FCL" && renderPartyBlock(t("Consignee Dock"), sharedConsigneeDock, t("Consignee Dock #"))}

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0B3C5D" }}>
                  {t("Vehicles")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("Total")} : {vehicles.length}
                </Typography>
              </Box>
            </Box>

            {!!vehicles.length && (() => {
              const totalVehicles = vehicles.length;
              const totalWeight = vehicles.reduce((s, v) => s + (Number(v?.weight) || 0), 0);
              const distinctTypes = [
                ...new Set(vehicles.map((v) => v?.vehicleType?.name || v?.vehicleType).filter(Boolean)),
              ];

              return (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                    <Chip size="small" color="primary" variant="outlined" label={`${t("Vehicles")}: ${totalVehicles}`} />
                    <Chip size="small" color="success" variant="outlined" label={`${t("Net Weight")}: ${totalWeight}`} />
                    {distinctTypes.map((type) => (
                      <Chip key={type} size="small" label={type} />
                    ))}
                  </Box>
                  {["LCL", "SPL"].includes(shipmentMode) && (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: 1.5,
                        mb: 2,
                        overflowX: "auto",
                        overflowY: "hidden",
                        pb: 1,
                        pr: 0.5,
                        scrollbarWidth: "thin",
                        "&::-webkit-scrollbar": {
                          height: 8,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#c8d0d8",
                          borderRadius: 8,
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#eef2f5",
                          borderRadius: 8,
                        },
                      }}
                    >
                      {shipmentItems.map((item, itemIndex) => renderVehicleDetailsCard(item, itemIndex, c))}
                    </Box>
                  )}
                  {shipmentMode === "FCL" && (
                  <Box sx={{ maxHeight: 260, overflow: "auto", border: "1px solid #eef2f5", borderRadius: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, background: "#f1f5f9" }}>{t("ID")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, background: "#f1f5f9", minWidth: 180 }}>{t("Vehicle")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, background: "#f1f5f9", minWidth: 160 }}>{t("Client")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, background: "#f1f5f9" }}>{t("VIN")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, background: "#f1f5f9", minWidth: 160 }}>{t("Destination")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, background: "#f1f5f9" }}>{t("Weight")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, background: "#f1f5f9" }}>{t("Fuel")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shipmentItems.map((item, vi) => {
                          const v = item?.vehicle;
                          if (!v) return null;
                          const desc = vehicleDescription(v) || "-";
                          const itemClient = getItemClient(item);
                          return (
                            <TableRow key={v.id || vi} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafcfe' } }}>
                              <TableCell>{v.id}</TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap title={desc}>
                                  {desc}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {v?.vehicleType?.name || ""}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{itemClient?.name || "-"}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {itemClient?.company || ""}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Typography variant="body2" noWrap title={v.vin}>
                                    {v.vin}
                                  </Typography>
                                  {v.vin && (
                                    <Tooltip title={t("Copy VIN")} arrow>
                                      <IconButton size="small" onClick={() => handleCopy(v.vin)} sx={{ ml: 0.5 }}>
                                        <ContentCopyIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>{vehicleDestination(v, c) || "-"}</TableCell>
                              <TableCell>{v.weight || "-"}</TableCell>
                              <TableCell>{v.fuelType?.name || v.fuelType || "-"}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                  )}
                </Box>
              );
            })()}
          </Paper>
        );
      })}
    </Box>
  );
};

CollapseView.propTypes = {
  row: PropTypes.object.isRequired,
  onOpenContainerPrint: PropTypes.func.isRequired,
};

export default CollapseView;
